import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, Video, Image as ImageIcon, Loader2, Check, Clock, Palette, Sparkles, Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { mediaService } from '../../services/mediaService';
import { iconService, IconResult } from '../../services/iconService';
import { LottieResult } from '../../services/lottieService';
import { IconSearchPanel } from '../icons/IconSearchPanel';
import { IconPreviewPanel } from '../icons/IconPreviewPanel';
import { LottieSearchPanel } from '../lottie/LottieSearchPanel';
import { LottiePreviewPanel } from '../lottie/LottiePreviewPanel';
import { UploadPanel } from './UploadPanel';
import { cn } from '../../lib/utils';

interface AssetHistoryItem {
  url: string;
  source: string;
  attribution: string | null;
  replacedAt: string | null;
}

interface PexelsSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: any) => void;
  type?: 'image' | 'video' | 'icon' | 'lottie' | 'upload';
  defaultQuery?: string;
  currentAsset?: {
    url: string;
    uniqueId: string;
  };
  assetHistory?: AssetHistoryItem[];
  slideAssets?: Array<{ url: string; uniqueId: string }>;
  recentProjectAssets?: Array<{ url: string; source: string; attribution: string }>;
  conversationId?: string;
  /** AI-suggested search terms from template metadata */
  suggestedSearchTerms?: string[];
  /** Semantic context describing the image's purpose */
  semanticContext?: string;
}

const ORIENTATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'square', label: 'Square' }
];

const COLOR_OPTIONS = [
  { value: 'all', label: 'All Colors', color: null },
  { value: 'red', label: 'Red', color: '#ef4444' },
  { value: 'orange', label: 'Orange', color: '#f97316' },
  { value: 'yellow', label: 'Yellow', color: '#eab308' },
  { value: 'green', label: 'Green', color: '#22c55e' },
  { value: 'turquoise', label: 'Turquoise', color: '#06b6d4' },
  { value: 'blue', label: 'Blue', color: '#3b82f6' },
  { value: 'violet', label: 'Violet', color: '#8b5cf6' },
  { value: 'pink', label: 'Pink', color: '#ec4899' },
  { value: 'brown', label: 'Brown', color: '#92400e' },
  { value: 'black', label: 'Black', color: '#000000' },
  { value: 'gray', label: 'Gray', color: '#6b7280' },
  { value: 'white', label: 'White', color: '#ffffff' }
];

export const PexelsSearchModal: React.FC<PexelsSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  type = 'image',
  defaultQuery = 'business',
  currentAsset,
  assetHistory,
  slideAssets,
  recentProjectAssets,
  conversationId,
  suggestedSearchTerms,
  semanticContext,
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState(defaultQuery);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [searchType, setSearchType] = useState<'image' | 'video' | 'icon' | 'lottie' | 'upload'>((type === 'icon' || type === 'video' || type === 'lottie' || type === 'upload') ? type : 'image');

  // Icon-specific state
  const [selectedIcon, setSelectedIcon] = useState<IconResult | null>(null);
  const [showIconPreview, setShowIconPreview] = useState(false);

  // Lottie-specific state
  const [selectedLottie, setSelectedLottie] = useState<LottieResult | null>(null);
  const [showLottiePreview, setShowLottiePreview] = useState(false);

  // Pagination state
  const [searchPage, setSearchPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filter state
  const [orientation, setOrientation] = useState<string>('all');
  const [selectedColor, setSelectedColor] = useState<string>('all');

  // Refs for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Deduplicate assets by URL across all sources
  const deduplicatedAssets = useMemo(() => {
    const seenUrls = new Map<string, boolean>();
    const result: Array<{
      url: string;
      section: 'current' | 'history' | 'slide' | 'recent';
      label: string;
      source?: string;
      attribution?: string | null;
      uniqueId?: string;
    }> = [];

    // Priority order: Current → History → Slide → Recent
    // First occurrence wins (preserves most relevant metadata)

    if (currentAsset && !seenUrls.has(currentAsset.url)) {
      seenUrls.set(currentAsset.url, true);
      result.push({
        url: currentAsset.url,
        section: 'current',
        label: 'Current',
        uniqueId: currentAsset.uniqueId
      });
    }

    assetHistory?.forEach((asset) => {
      if (!seenUrls.has(asset.url)) {
        seenUrls.set(asset.url, true);
        const label = asset.source === 'original'
          ? 'Original'
          : asset.replacedAt
            ? 'Previous'
            : 'Historical';
        result.push({
          url: asset.url,
          section: 'history',
          label,
          source: asset.source,
          attribution: asset.attribution
        });
      }
    });

    slideAssets?.forEach((asset, index) => {
      if (!seenUrls.has(asset.url)) {
        seenUrls.set(asset.url, true);
        result.push({
          url: asset.url,
          section: 'slide',
          label: `Slide asset ${index + 1}`,
          uniqueId: asset.uniqueId
        });
      }
    });

    recentProjectAssets?.forEach((asset, index) => {
      if (!seenUrls.has(asset.url)) {
        seenUrls.set(asset.url, true);
        result.push({
          url: asset.url,
          section: 'recent',
          label: `Recent ${index + 1}`,
          source: asset.source,
          attribution: asset.attribution
        });
      }
    });

    console.log(`🎯 Deduplicated ${result.length} unique assets from ${(assetHistory?.length || 0) + (slideAssets?.length || 0) + (recentProjectAssets?.length || 0) + (currentAsset ? 1 : 0)} total`);

    return result;
  }, [currentAsset, assetHistory, slideAssets, recentProjectAssets]);

  // Format video duration from seconds to MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate aspect ratio label from dimensions
  const getAspectRatioLabel = (width: number, height: number): string => {
    const ratio = width / height;
    if (Math.abs(ratio - 16/9) < 0.1) return '16:9';
    if (Math.abs(ratio - 4/3) < 0.1) return '4:3';
    if (Math.abs(ratio - 1) < 0.1) return '1:1';
    if (Math.abs(ratio - 3/2) < 0.1) return '3:2';
    if (Math.abs(ratio - 21/9) < 0.1) return '21:9';
    if (ratio > 1.5) return 'Wide';
    if (ratio < 0.7) return 'Tall';
    return `${Math.round(ratio * 10) / 10}:1`;
  };

  // State for video preview on hover
  const [hoveredVideoId, setHoveredVideoId] = useState<string | number | null>(null);

  // Build search query with filters
  const buildSearchQuery = useCallback(() => {
    let query = searchQuery.trim();

    if (searchType === 'image' && orientation !== 'all') {
      query += ` orientation:${orientation}`;
    }

    if (selectedColor !== 'all') {
      query += ` color:${selectedColor}`;
    }

    return query;
  }, [searchQuery, orientation, selectedColor, searchType]);

  // Main search function with pagination
  const searchPexels = useCallback(async (page = 1, append = false) => {
    if (mediaService.getAvailableProviders().length === 0) {
      setError('No media providers configured. Please add API keys (VITE_PEXELS_API_KEY, VITE_PIXABAY_API_KEY, or VITE_UNSPLASH_ACCESS_KEY) to your .env file.');
      return;
    }

    const query = buildSearchQuery();
    if (!query.trim()) return;

    // Set loading state
    if (page === 1) {
      setLoading(true);
      setResults([]);
      setSearchPage(1);
    } else {
      setIsLoadingMore(true);
    }

    setError(null);

    try {
      const perPage = 15; // Per provider, so total will be ~45 for images (3 providers)

      if (searchType === 'video') {
        // Use aggregated search from all video-supporting providers (Pexels, Pixabay)
        const response = await mediaService.searchVideosFromAllProviders(query, page, perPage);

        if (append) {
          setResults(prev => [...prev, ...(response.videos || [])]);
        } else {
          setResults(response.videos || []);
        }

        setTotalResults(response.total_results || 0);
        setHasMoreResults(!!response.next_page);
      } else {
        // Use aggregated search from all providers (Unsplash, Pexels, Pixabay)
        const response = await mediaService.searchPhotosFromAllProviders(query, page, perPage);

        if (append) {
          setResults(prev => [...prev, ...(response.photos || [])]);
        } else {
          setResults(response.photos || []);
        }

        setTotalResults(response.total_results || 0);
        setHasMoreResults(!!response.next_page);
      }

      setSearchPage(page);
    } catch (err) {
      console.error('Stock media search error:', err);
      setError('Failed to search stock media. Please check your API keys.');
      setResults([]);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [buildSearchQuery, searchType]);

  // Load more results
  const loadMoreResults = useCallback(() => {
    if (!isLoadingMore && hasMoreResults && !loading) {
      searchPexels(searchPage + 1, true);
    }
  }, [searchPage, hasMoreResults, isLoadingMore, loading, searchPexels]);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!isOpen || !hasMoreResults || isLoadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreResults();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen, hasMoreResults, isLoadingMore, loading, loadMoreResults]);

  // Perform initial search when modal opens or filters change
  useEffect(() => {
    if (isOpen && searchQuery) {
      searchPexels(1, false);
    }
  }, [isOpen, orientation, selectedColor, searchType]);

  // Handle search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchPexels(1, false);
    }
  };

  // Handle asset selection
  const handleSelect = (asset: any) => {
    setSelectedAsset(asset);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedAsset) {
      onSelect(selectedAsset);
      onClose();
    }
  };

  // Handle icon selection from IconSearchPanel
  const handleIconSelect = (icon: IconResult) => {
    setSelectedIcon(icon);
    setShowIconPreview(true);
  };

  // Handle icon confirmation from IconPreviewPanel
  const handleIconConfirm = (icon: IconResult, color: string, size: number) => {
    const svgUrl = iconService.getIconUrl(icon.id, color, size);
    onSelect({
      src: { original: svgUrl, large: svgUrl },
      photographer: icon.collectionName,
      source: 'iconify',
      isIcon: true,
      iconId: icon.id,
      iconColor: color,
      iconSize: size,
    });
    onClose();
  };

  // Handle icon preview cancel
  const handleIconPreviewCancel = () => {
    setShowIconPreview(false);
    setSelectedIcon(null);
  };

  // Handle Lottie selection from LottieSearchPanel
  const handleLottieSelect = (animation: LottieResult) => {
    setSelectedLottie(animation);
    setShowLottiePreview(true);
  };

  // Handle Lottie confirmation from LottiePreviewPanel
  const handleLottieConfirm = (animation: LottieResult) => {
    onSelect({
      src: { original: animation.jsonUrl, large: animation.jsonUrl },
      photographer: animation.author,
      source: 'lottiefiles',
      isLottie: true,
      lottieId: animation.id,
      previewGif: animation.previewGif,
    });
    onClose();
  };

  // Handle Lottie preview cancel
  const handleLottiePreviewCancel = () => {
    setShowLottiePreview(false);
    setSelectedLottie(null);
  };

  if (!isOpen) return null;

  // Handle reuse of existing assets
  const handleReuseAsset = (url: string, source: string = 'reused', attribution: string | null = null) => {
    // Clean up attribution text from any provider
    const cleanAttribution = attribution
      ?.replace('Photo by ', '')
      .replace(' from Pexels', '')
      .replace(' from Pixabay', '')
      .replace(' from Unsplash', '') || source;

    onSelect({
      src: { original: url, large: url },
      photographer: cleanAttribution,
      // Indicate this is a reused asset
      reused: true,
      source
    });
    onClose();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      {/* Quick Select Section - Compact */}
      {deduplicatedAssets.length > 0 && (
        <div className="border-b border-border/50 px-3 py-2.5 bg-muted/20 dark:bg-muted/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap uppercase tracking-wide">Quick Select</span>
            <div className="flex gap-2 overflow-x-auto">
              {deduplicatedAssets.map((asset, index) => (
                <div
                  key={`asset-${index}`}
                  className="relative group cursor-pointer flex-shrink-0"
                  onClick={() => handleReuseAsset(asset.url, asset.source || asset.section, asset.attribution)}
                  title={`${asset.label}${asset.source ? ` (${asset.source})` : ''}`}
                >
                  <div className={cn(
                    "w-11 h-11 rounded-lg overflow-hidden transition-all shadow-sm",
                    asset.section === 'current'
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background"
                      : "ring-1 ring-border/50 hover:ring-primary/70 dark:ring-border/30"
                  )}>
                    <img
                      src={asset.url}
                      alt={asset.label}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                    />
                  </div>
                  {asset.section === 'current' && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-sm">
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI-Suggested Search Terms - GLOBAL (above tabs, always visible when available) */}
      {suggestedSearchTerms && suggestedSearchTerms.length > 0 && searchType !== 'upload' && (
        <div className="border-b border-border/50 px-3 py-2.5 bg-gradient-to-r from-amber-500/5 to-transparent flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              AI Suggestions:
            </span>
            {suggestedSearchTerms.map((term, i) => (
              <button
                key={i}
                onClick={() => {
                  setSearchQuery(term);
                  // Auto-search based on current tab
                  if (searchType === 'image' || searchType === 'video') {
                    searchPexels(1, false);
                  }
                  // For icon/lottie, the child components will auto-search via externalQuery
                }}
                className={cn(
                  "px-2.5 py-1 text-xs rounded-full transition-all font-medium",
                  searchQuery === term
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-background text-foreground hover:bg-muted border border-border/50 hover:border-primary/50"
                )}
              >
                {term}
              </button>
            ))}
          </div>
          {semanticContext && (
            <p className="text-[10px] text-muted-foreground mt-1.5 line-clamp-1 italic pl-5">
              Context: {semanticContext}
            </p>
          )}
        </div>
      )}

      {/* Media Type Toggle - Modern pill design */}
      <div className="border-b border-border/50 px-3 py-2.5 bg-background flex-shrink-0">
        <div className="inline-flex rounded-lg bg-muted/50 dark:bg-muted/30 p-0.5">
          <button
            onClick={() => setSearchType('image')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 rounded-md",
              searchType === 'image'
                ? "bg-background dark:bg-zinc-800 text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <ImageIcon className="h-3.5 w-3.5" />
            <span>Images</span>
          </button>
          <button
            onClick={() => setSearchType('video')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 rounded-md",
              searchType === 'video'
                ? "bg-background dark:bg-zinc-800 text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Video className="h-3.5 w-3.5" />
            <span>Videos</span>
          </button>
          <button
            onClick={() => setSearchType('icon')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 rounded-md",
              searchType === 'icon'
                ? "bg-background dark:bg-zinc-800 text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Palette className="h-3.5 w-3.5" />
            <span>Icons</span>
          </button>
          <button
            onClick={() => setSearchType('lottie')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 rounded-md",
              searchType === 'lottie'
                ? "bg-background dark:bg-zinc-800 text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Lottie</span>
          </button>
          {conversationId && (
            <button
              onClick={() => setSearchType('upload')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 rounded-md",
                searchType === 'upload'
                  ? "bg-background dark:bg-zinc-800 text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Upload className="h-3.5 w-3.5" />
              <span>Upload</span>
            </button>
          )}
        </div>
      </div>

      {/* Upload Tab Content */}
      {searchType === 'upload' && conversationId ? (
        <UploadPanel
          conversationId={conversationId}
          onSelect={onSelect}
          onClose={onClose}
        />
      ) : /* Icon Tab Content */
      searchType === 'icon' ? (
        <div className="flex flex-1 min-h-0">
          {/* Icon Search Panel */}
          <div className={cn(
            "flex-1 min-h-0",
            showIconPreview ? "hidden md:flex md:flex-1" : "flex"
          )}>
            <IconSearchPanel
              onSelect={handleIconSelect}
              defaultStyle="line"
              externalQuery={searchQuery}
              onQueryChange={setSearchQuery}
            />
          </div>

          {/* Icon Preview Panel */}
          {showIconPreview && selectedIcon && (
            <div className="w-full md:w-80 flex-shrink-0 min-h-0 overflow-y-auto">
              <IconPreviewPanel
                icon={selectedIcon}
                onConfirm={handleIconConfirm}
                onCancel={handleIconPreviewCancel}
              />
            </div>
          )}
        </div>
      ) : searchType === 'lottie' ? (
        <div className="flex flex-1 min-h-0">
          {/* Lottie Search Panel */}
          <div className={cn(
            "flex-1 min-h-0",
            showLottiePreview ? "hidden md:flex md:flex-1" : "flex"
          )}>
            <LottieSearchPanel
              onSelect={handleLottieSelect}
              externalQuery={searchQuery}
              onQueryChange={setSearchQuery}
            />
          </div>

          {/* Lottie Preview Panel */}
          {showLottiePreview && selectedLottie && (
            <div className="w-full md:w-80 flex-shrink-0 min-h-0 overflow-y-auto">
              <LottiePreviewPanel
                animation={selectedLottie}
                onConfirm={handleLottieConfirm}
                onCancel={handleLottiePreviewCancel}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Image/Video Search Bar and Filters */}
          <div className="border-b border-border/50 px-3 py-3 bg-background flex-shrink-0 space-y-2.5">
            {/* Row 1: Search + Button */}
            <div className="flex gap-2 items-center">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={`Search ${searchType === 'video' ? 'videos' : 'images'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="pl-9 pr-3 h-9 text-sm bg-muted/30 dark:bg-muted/20 border-border/50 focus:border-primary/50"
                  autoFocus
                />
              </div>
              <Button
                onClick={() => searchPexels(1, false)}
                disabled={loading || !searchQuery.trim()}
                size="sm"
                className="h-9 px-4"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

        {/* Row 2: Filters + Results Count - All inline */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Orientation Filter (Images only) */}
          {searchType === 'image' && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Size:</span>
              <div className="inline-flex rounded-md bg-muted/40 dark:bg-muted/20 p-0.5">
                {ORIENTATION_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setOrientation(option.value)}
                    className={cn(
                      "px-2 py-0.5 text-[10px] font-medium transition-all rounded-sm",
                      orientation === option.value
                        ? "bg-background dark:bg-zinc-700 text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Filter - Smaller swatches */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Color:</span>
            <div className="flex gap-1">
              {COLOR_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedColor(option.value)}
                  className={cn(
                    "w-4 h-4 rounded-full transition-all",
                    selectedColor === option.value
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-110"
                      : "hover:scale-110",
                    option.color ? '' : 'bg-primary'
                  )}
                  style={{
                    backgroundColor: option.color || undefined,
                    border: option.color === '#ffffff' ? '1px solid #d1d5db' : 'none',
                    boxShadow: option.color !== '#ffffff' ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : 'none'
                  }}
                  title={option.label}
                />
              ))}
            </div>
          </div>

          {/* Results Count - Inline */}
          {totalResults > 0 && (
            <div className="text-[10px] text-muted-foreground ml-auto tabular-nums">
              {results.length} of {totalResults.toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Results Grid - More compact padding */}
      <div className="flex-1 overflow-y-auto px-2 py-2 min-h-0">
        {error ? (
          <div className="text-center py-8">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
              {searchType === 'video' ? (
                <Video className="h-6 w-6 text-muted-foreground" />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              No {searchType === 'video' ? 'videos' : 'images'} found. Try a different search term.
            </p>
          </div>
        ) : (
          <>
            {/* Enhanced Grid - More columns, tighter gap */}
            <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-1.5">
              {results.map((asset) => {
                const isVideo = searchType === 'video';
                const thumbnail = isVideo
                  ? asset.image
                  : (asset.src?.medium || asset.src?.original);
                const duration = isVideo && asset.duration;
                const aspectRatio = asset.width && asset.height ? getAspectRatioLabel(asset.width, asset.height) : null;
                const isHovered = hoveredVideoId === asset.id;

                return (
                  <div
                    key={asset.id}
                    className={cn(
                      "relative group cursor-pointer rounded overflow-hidden transition-all",
                      selectedAsset?.id === asset.id
                        ? "ring-2 ring-primary ring-offset-1"
                        : "hover:ring-1 hover:ring-primary/50"
                    )}
                    onClick={() => handleSelect(asset)}
                    onMouseEnter={() => isVideo && setHoveredVideoId(asset.id)}
                    onMouseLeave={() => isVideo && setHoveredVideoId(null)}
                  >
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      {/* Video Preview on Hover */}
                      {isVideo && isHovered && asset.video_files?.[0]?.link ? (
                        <video
                          src={asset.video_files.find((f: any) => f.quality === 'sd')?.link || asset.video_files[0].link}
                          className="w-full h-full object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          src={thumbnail}
                          alt={asset.alt || `${searchType} from stock media`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      )}

                      {/* Top badges row */}
                      <div className="absolute top-1 left-1 right-1 flex justify-between items-start pointer-events-none">
                        {/* Provider Badge - Left */}
                        {asset.source && !selectedAsset?.id === asset.id && (
                          <span className="bg-black/60 text-white text-[8px] px-1 py-0.5 rounded capitalize backdrop-blur-sm">
                            {asset.source}
                          </span>
                        )}
                        {/* Selected Check - Left */}
                        {selectedAsset?.id === asset.id && (
                          <span className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </span>
                        )}
                        {/* HD/Aspect Badge - Right */}
                        <span className="flex gap-0.5 ml-auto">
                          {isVideo && asset.video_files?.some((f: any) => f.quality === 'hd') && (
                            <span className="bg-green-600 text-white text-[8px] px-1 py-0.5 rounded font-bold">
                              HD
                            </span>
                          )}
                          {!isVideo && aspectRatio && (
                            <span className="bg-black/60 text-white text-[8px] px-1 py-0.5 rounded backdrop-blur-sm">
                              {aspectRatio}
                            </span>
                          )}
                        </span>
                      </div>

                      {/* Bottom info - Duration for videos, dimensions on hover for images */}
                      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
                        {isVideo && duration ? (
                          <div className="bg-gradient-to-t from-black/80 to-transparent p-1">
                            <span className="text-white text-[9px] font-medium flex items-center gap-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {formatDuration(duration)}
                            </span>
                          </div>
                        ) : (
                          <div className="bg-gradient-to-t from-black/70 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-[9px] truncate">
                              {asset.photographer || asset.user?.name}
                            </p>
                            {asset.width && asset.height && (
                              <p className="text-white/70 text-[8px]">{asset.width}×{asset.height}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Play icon for videos */}
                      {isVideo && !isHovered && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center backdrop-blur-sm">
                            <Video className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More - Compact */}
            {hasMoreResults && (
              <div ref={loadMoreRef} className="py-4 flex justify-center">
                {isLoadingMore ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-xs">Loading...</span>
                  </div>
                ) : (
                  <Button
                    onClick={loadMoreResults}
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                  >
                    Load More
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

          {/* Fixed Footer with Actions - Compact (only for image/video) */}
          <div className="flex-shrink-0 border-t border-border/50 px-4 py-3 flex items-center justify-between bg-muted/10 dark:bg-muted/5">
            <div className="text-xs text-muted-foreground">
              {selectedAsset && (
                <span className="flex items-center gap-1.5">
                  <span className="font-medium text-foreground">
                    {searchType === 'video' ? selectedAsset.user?.name : selectedAsset.photographer}
                  </span>
                  {selectedAsset.width && selectedAsset.height && (
                    <span className="text-muted-foreground">• {selectedAsset.width}×{selectedAsset.height}</span>
                  )}
                  {selectedAsset.source && (
                    <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] uppercase font-medium">{selectedAsset.source}</span>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} className="h-8">
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedAsset}
                size="sm"
                className="h-8"
              >
                Use This
              </Button>
            </div>
          </div>
        </>
      )}  {/* End of searchType !== 'icon' conditional */}
    </div>
  );
};